import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the service worker so the app is installable as a PWA.
// The SW is network-first for HTML/JS/CSS so users never get stale code.
//
// When a new SW is installed, it sits in `waiting` until the user accepts.
// We dispatch a `sw-update-available` window event so a banner can prompt
// them to refresh (see `<SwUpdateBanner />` in App.jsx).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        // If a new worker is already waiting (e.g. user just opened a tab
        // after a deploy), surface immediately.
        if (registration.waiting && navigator.serviceWorker.controller) {
          window.dispatchEvent(new CustomEvent('sw-update-available', {
            detail: { worker: registration.waiting },
          }));
        }
        // And listen for future updates.
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent('sw-update-available', {
                detail: { worker: newWorker },
              }));
            }
          });
        });

        // Background update polling — every 5 minutes the browser asks
        // the server for a fresh copy of `/service-worker.js`. If the hash
        // matches, the server 304s and the browser does nothing. If a
        // new SW has been deployed, the browser downloads it and fires
        // the `updatefound` handler above, which surfaces the banner.
        // Also re-checks on tab focus so a user returning from a coffee
        // break sees fresh code immediately rather than 5 minutes later.
        const pollForUpdate = () => {
          registration.update().catch(() => {});
        };
        setInterval(pollForUpdate, 5 * 60 * 1000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') pollForUpdate();
        });
      })
      .catch((err) => {
        // Don't crash the app if registration fails (e.g. dev with HTTP).
        console.warn('[SW] register failed:', err);
      });

    // When the new SW takes control (after SKIP_WAITING + activate), reload
    // exactly once so the user lands on the fresh code instantly.
    let reloadingForUpdate = false;
    const reloadOnce = () => {
      if (reloadingForUpdate) return;
      reloadingForUpdate = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);
    // The SW also pings us via postMessage on activate so we can reload
    // even when the page first loaded under a previous SW (e.g. the user
    // had a stale tab open across a deploy).
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_ACTIVATED') {
        reloadOnce();
      }
    });
  });
}
